import { useEffect, useMemo, useState } from "react";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import { getCookie, setCookie } from "@directv-digital/dds.core.commons";
import ReportingService from "src/services/ReportingService";
import { docReady } from "src/utils/Document";
import Head from "../../../src/components/Head";
import DocumentEventHandler from "@directv-digital/dds.ui.document-event-handler";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Gradiant_Colors } from "../../../src/components/Background/BackgrondProps";
import { BrandColor } from "../../../src/constants/CssProperties";
import { getCurrentBreakpoint } from "../../../src/utils/Breakpoint/Breakpoint";
import { trackCartSummary } from "src/utils/cart-summary/fire-cart-summary";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvFooter from "@directv-digital/dds.ui.dtv-footer";

import { SignalStrengthPageData } from "src/cms/signalStrengthFeed";

const DtvGlobalHeader = dynamic(
  () => import("@directv-digital/dds.ui.global-header-v3")
);

// Lazy-load the interactive tool (rebass + react-redux + redux-thunk +
// d3-delaunay + polybooljs) so it stays out of the page's first-load JS.
// ssr:false is safe: the tool renders no channels until the user enters a
// location, and SEO meta is owned by <Head>.
const SignalStrength = dynamic(
  () => import("src/components/SignalStrengthV2/SignalStrength"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: "70vh" }} />
  }
);

const { publicRuntimeConfig } = getConfig();

import { deleteCookie } from "src/utils/deleteCookie";

interface IndexProps {
  data: any;
  err?: { statusCode: number };
  feed: any;
  isOrc: boolean;
  pagePath: string;
  page: string;
  root: string;
  seo?: any;
  zipCode?: number;
  depVersions?: any;
  userAgent?: string; // to-do: need this?
  globalNavHeader?: any;
  globalNavLinkFarm?: any;
  idleTimerModal?: any;
  is404Page?: boolean;
  reqQueryParams?: any;
  classes?: any;
  isUserAuthenticated?: boolean;
  disableGNav?: boolean;
  // isV2GNav?: boolean;
  disableSkinnyGNav?: boolean;
  disableLinkFarm?: boolean;
  disableGlobalFooter?: boolean;
  // isV2LinkFarm?: boolean;
  // isV2Footer?: boolean;
  disableAuxNav?: boolean;
  disableBusinessLink?: boolean;
  recognizedUserData?: any;
  // auxNav?: any;
  helpCenterInfoLabel?: any;
  customerCareNum?: any;
  customerCareNumLabel?: any;
  disableTFN?: boolean;
  pageThemeObj?: any;
  oem?: any;
  globalOverrides?: any;
  linkfarmdata?: any;
  isDCP: boolean;
  apiPaths: any;
  breadcrumbHomeURL: string;
  appVariant: string;
  hydratedState?: any;
  isOrigin: string;
  env: string;
  sbFeed: any;
}

// WAT-10870: prop-driven body background (gradient/color from PAGEBACKGROUNDS,
// else the page default). Applied via the <GlobalStyles> render below — replaces
// the removed legacy JSS global body rule. Dead pageContainer class also removed.
const getBodyBackground = (props: any) => {
  const pageBackgrounds =
    (props.data &&
      props.data.find(item => item.type === "PAGEBACKGROUNDS")) ||
    null;
  const pageBody = pageBackgrounds && pageBackgrounds.pageBody;
  if (pageBody && pageBody.gradient && Gradiant_Colors[pageBody.gradient]) {
    return Gradiant_Colors[pageBody.gradient];
  }

  if (pageBody && pageBody.color) {
    return BrandColor[pageBody.color.toLowerCase()] || pageBody.color;
  }

  return props?.pagePath?.includes("404") ? "#000000" : "#FFFFFF";
};

const DTVUFComSignalStrengthIndex: React.FC<IndexProps> = (props: any) => {
  const appEnv = publicRuntimeConfig?.local;
  const bodyBackground = getBodyBackground(props);
  const pageProperties: any = {
    pageProperties: {
      title: "pageProperties",
      elements: {
        analyticsPageLoad: {
          analytics: []
        },
        meta: [
          {
            name: "keywords",
            content:
              "broadcast, channel connector, local channel, local broadcast, local channel connector, reception, signal, signal detector, signal strength, tool"
          },
          {
            name: "description",
            content:
              "Use this tool to see over-the-air signal strength for your Local Channel Connector."
          },
          {
            name: "robots",
            content: "INDEX,FOLLOW"
          },
          {
            property: "og:title",
            content: "Local Broadcast Signal Strength Detector | DIRECTV"
          },
          {
            property: "og:url",
            content: "https://www.directv.com/tools/signal-strength/"
          },
          {
            property: "og:image",
            content:
              "https://www.directv.com/dtvassets/logos/DIRECTV-1200x628.png"
          },
          {
            property: "og:type",
            content: "website"
          },
          {
            property: "og:site_name",
            content: "DIRECTV"
          },
          {
            property: "og:description",
            content:
              "Use this tool to see over-the-air signal strength for your Local Channel Connector."
          },
          {
            name: "twitter:card",
            content: "summary"
          },
          {
            name: "twitter:description",
            content:
              "Use this tool to see over-the-air signal strength for your Local Channel Connector."
          },
          {
            name: "twitter:image",
            content:
              "https://www.directv.com/dtvassets/logos/DIRECTV-1200x1200.png"
          },
          {
            name: "twitter:title",
            content: "Local Broadcast Signal Strength Detector | DIRECTV"
          },
          {
            name: "twitter:url",
            content: "https://www.directv.com/tools/signal-strength/"
          },
          {
            name: "twitter:creator",
            content: "@DIRECTV"
          },
          {
            name: "twitter:site",
            content: "@DIRECTV"
          }
        ],
        link: [
          {
            rel: "canonical",
            href: "https://www.directv.com/tools/signal-strength/"
          }
        ],
        id: "pageProperties",
        disableLanguageLink: false,
        title: "Local Broadcast Signal Strength Detector | DIRECTV",
        script: []
      }
    }
  };
  const [showCustomerNav, setshowCustomerNav] = useState(
    props.recognizedUserData ?
      !!props.page.includes("recognized") :
      false
  ); // render unauth at server all the time not to cache recog state
  // Note: check where is it used and remove if not needed
  const _pageBackground = useMemo(() => {
    const pageBackgrounds =
      (props?.data &&
        props.data.find(item => item.type === "PAGEBACKGROUNDS")) ||
      null;
    const pageContainer = pageBackgrounds && pageBackgrounds.pageContainer;
    if (
      pageContainer &&
      pageContainer.gradient &&
      Gradiant_Colors[pageContainer.gradient]
    ) {
      return Gradiant_Colors[pageContainer.gradient];
    }

    if (pageContainer && pageContainer.color) {
      return (
        BrandColor[pageContainer.color.toLowerCase()] || pageContainer.color
      );
    }

    return props?.pagePath?.includes("channel-lineup") ? "white" : "inherit";
  }, [props?.data, props?.pagePath]);

  console.log("evolv hydratedState", props.hydratedState);

  const [_clientSideIsUserAuth, setClientSideIsUserAuth] = useState(
    props.isUserAuthenticated
  );
  const [_clientSideRecognizedUserData, setClientSideRecognizedUserData] =
    useState(props.recognizedUserData);

  useEffect(() => {
    const cAuthNStateCookie = getCookie("cAuthNState");
    const recogmeCookie = getCookie("recogme");
    const accTypeInCtxCookie = getCookie("accTypeInCtx");
    const authenicated =
      cAuthNStateCookie &&
      cAuthNStateCookie !== "en" &&
      typeof accTypeInCtxCookie !== "undefined";
    const recognized = recogmeCookie && recogmeCookie !== "";

    setshowCustomerNav(authenicated || recognized);
    setClientSideIsUserAuth(cAuthNStateCookie && cAuthNStateCookie !== "en");

    if (
      (authenicated || recognized) &&
      !props.recognizedUserData &&
      accTypeInCtxCookie
    ) {
      if (
        ["ATTTV", "ATTTV_NONVIDEO", "ATTTV_ENABLER"].includes(
          accTypeInCtxCookie
        )
      ) {
        setClientSideRecognizedUserData({ accountType: "stream" });
      } else if (["STMS", "NEWCO", "ENABLER"].includes(accTypeInCtxCookie)) {
        setClientSideRecognizedUserData({ accountType: "satellite" });
      }
    }

    console.debug("<< Event listener added");
    window.addEventListener("load", windowLoadCallBack);

    getData();

    return () => {
      if (window) {
        window.removeEventListener("load", windowLoadCallBack);
      }
    };
  }, []);

  // Delete cookie "intent" if found in any page of Upper Funnel for shop team WAT-5799
  useEffect(() => {
    /**
      * @see {@link https://directv-it.atlassian.net/browse/WAT-8043}
      */
    const params = new URLSearchParams(window.location.search);
    if (params?.get('retainCookie') !== 'true') {
      deleteCookie('intent');
    }
  }, []);

  async function getData() {
    const service: any = new ReportingService();
    if (process.browser && window && window.DataMappingInterface) {
      window.DataMappingInterface.disableAutoPageLoad();
    }
    docReady(() => {
      service.sendImpressions(
        props || [],
        getCurrentBreakpoint(),
        props.page,
        props.pagePath,
        props.root,
        props.reqQueryParams
      );

      // WAT-11358: GA4 view_cart / cart_summary. This page passes no showCart to
      // the header, so no cart icon renders here — confirmed in the browser. The AC
      // scopes the event to pages that display a cart, so it must not fire.
      // Still called rather than skipped so the trigger sees this page load and its
      // once-per-load key advances.
      trackCartSummary({ cartIsDisplayed: false });
    });
  }

  const windowLoadCallBack = () => {
    // On Load triggered and third party event sent to load inqChat, Foresee and Online Opinion
    console.debug("<< Window Load Triggered >>", document.readyState);
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent("marTechTrigger"));
    }, 500);
    console.debug("<< marTechTrigger Triggered >>", document.readyState);

    // setting oem cookie
    if (props?.oem) {
      let cookieName;
      let domainName;
      if (props?.page.includes("stream/")) {
        cookieName = "oem";
        domainName = ".directv.com";
      } else if (props?.page.includes("satellite/")) {
        cookieName = "oemsat";
      }

      // Only write the oem cookie when a name was resolved — otherwise a cookie
      // literally named "undefined" is written on non-stream/non-satellite pages.
      if (cookieName) {
        setCookie(
          cookieName,
          props?.oem?.elements?.cookieValue ?? props?.oem?.elements?.partnerName,
          "/",
          1,
          domainName
        );
      }
    }

    // setting agentId cookie
    if (props.reqQueryParams?.agentId) {
      setCookie(
        "agentId",
        props.reqQueryParams?.agentId,
        "/",
        1,
        window.location.hostname,
        true
      );
    } else {
      document.cookie =
        "agentId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  };

  if (props.sbFeed) {
    console.log("cms_type sb");
  } else {
    console.log("cms_type aem");
  }

  return (
    <>
      <DocumentEventHandler />
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            fontSize: "0.875rem",
            background: bodyBackground
          },
          p: {
            margin: "0.5em 0em 0.5em"
          }
        }}
      />
      <meta
        content="width=device-width, height=device-height, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no"
        name="viewport"
      />
      <Head data={pageProperties.pageProperties} flag="head" />

      <DtvGlobalHeader
        data={props.gnHeaderCms}
        disableAuxNav={true}
        env={appEnv as any}
        isAuth={props.isUserAuthenticated}
        isLightTheme={true}
        recognised={showCustomerNav}
      ></DtvGlobalHeader>
      <DtvBox
        sx={{
          display: "block"
        }}
      >
        <DtvBox
          sx={{
            display: "block"
          }}
        >
          <SignalStrength
            isOutdoor="false"
            pageFeed={
              props.sbFeed ?
                props.sbFeed.pageContent :
                SignalStrengthPageData.pageContent
            }
          />
        </DtvBox>
        <DtvFooter data={props?.footer}  enableLangSupport={false} />
      </DtvBox>
    </>
  );
};

export const getServerSideProps = async ctx => {
  const validateCAuthNStateCookie = (cAuthNStateCookie: string): boolean => {
    let isAuthenticated = false;
    if (cAuthNStateCookie && cAuthNStateCookie !== "en") {
      isAuthenticated = true;
    }
    return isAuthenticated;
  };

  const isAuthenticatedUser = (req: any) => {
    let cAuthNState;
    const cookiesObj =
      req?.headers && req?.headers["idpctx-cookie"] ?
        req?.headers["idpctx-cookie"] :
        req?.headers && req?.headers.cookie ?
          req?.headers.cookie :
          "";
    const cookiesArr = cookiesObj?.split(";");
    // return header && header.indexOf('cAuthNState') > -1 && header.substr(header.indexOf('cAuthNState'))
    cookiesArr?.forEach(cookie => {
      if (cookie.indexOf("cAuthNState") !== -1) {
        [, cAuthNState] = cookie.split("=");
      }
    });
    return validateCAuthNStateCookie(cAuthNState || ``);
  };

  if (ctx.req) {
    const additionalParams = ctx?.req?.additionalParams;
    const page = additionalParams?.page;
    const isUserLoggedIn = isAuthenticatedUser(ctx.req);
    const isOrigin = "no";
    const env = "dev1";

    const sbFeed = additionalParams?.pageComponentsData?.components?.find(
      component => Object.keys(component?.props?.pageContent || {})?.length > 0
    )?.props;

    const props = {
      isOrigin,
      env: env || null,
      gnHeaderCms: additionalParams?.header,
      footer: additionalParams?.footer,
      isUserAuthenticated: isUserLoggedIn || false,
      sbFeed: sbFeed || null,
      dataSource: additionalParams?.dataSource || ""
    };
    console.log("props ready to be returned: ", page);
    return { props };
  }
};

export default DTVUFComSignalStrengthIndex;
